import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  AdminPanelSettings,
  Badge,
  Close,
  CorporateFare,
  Download,
  Edit,
  FilterList,
  Groups,
  LockOpen,
  MailOutline,
  ManageAccounts,
  PeopleAlt,
  PersonSearch,
  PhoneIphone,
  Refresh,
  RestartAlt,
  Save,
  Search,
  VerifiedUser,
  Visibility,
} from '@mui/icons-material';
import axios from 'axios';
import { motion } from 'framer-motion';
import LoadingScreen from './LoadingScreen';
import './AdminUserManagement.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'pending_approval', label: 'Pending approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'activated', label: 'Activated' },
  { value: 'unlocked', label: 'Unlocked' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'rejected', label: 'Rejected' },
];

const ACCESS_OPTIONS = [
  { value: 'all', label: 'All access levels' },
  { value: 'admin', label: 'Admin' },
  { value: 'user', label: 'User' },
];

const ROLE_FILTER_OPTIONS = [
  { value: 'all', label: 'All user types' },
  { value: 'provider', label: 'Providers only' },
  { value: 'founder', label: 'Founders only' },
  { value: 'community', label: 'Local community' },
  { value: 'incomplete', label: 'Profile incomplete' },
];

const SORT_OPTIONS = [
  { value: 'first_name', label: 'Name' },
  { value: 'email', label: 'Email' },
  { value: 'company', label: 'Company' },
  { value: 'role', label: 'Role' },
  { value: 'status', label: 'Status' },
  { value: 'modified_date', label: 'Last modified' },
];

const statusTone = {
  unlocked: 'success',
  activated: 'info',
  approved: 'secondary',
  pending_approval: 'warning',
  inactive: 'default',
  rejected: 'error',
};

const statusLabel = (value) => {
  if (!value) return 'Unknown';
  return String(value).replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const safeLower = (value) => String(value || '').toLowerCase();

const booleanish = (value) => value === true || value === 1 || value === '1' || value === 'true';

const parseList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value !== 'string') return [];

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.filter(Boolean);
  } catch (error) {
    // Some older records may be comma-separated plain text instead of JSON.
  }

  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const formatDate = (value) => {
  if (!value) return 'Not updated';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const initialsFor = (user) => {
  const first = user?.first_name?.trim()?.[0] || '';
  const last = user?.last_name?.trim()?.[0] || '';
  return `${first}${last}`.toUpperCase() || 'UC';
};

const userSearchBlob = (user) => [
  user.first_name,
  user.last_name,
  user.email,
  user.mobile,
  user.company,
  user.industry,
  user.role,
  user.status,
  user.accesslevel,
  user.modified_by,
  user.other_help,
  user.other_support,
  user.intent,
  user.localcommunity,
  ...parseList(user.helpfields),
  ...parseList(user.supporter_lst),
].join(' ');

const exportUsersToCsv = (users) => {
  const headers = [
    'id',
    'first_name',
    'last_name',
    'email',
    'mobile',
    'company',
    'industry',
    'role',
    'status',
    'accesslevel',
    'isprovider',
    'stack_holder',
    'profileupdate',
    'modified_date',
  ];

  const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const csv = [
    headers.join(','),
    ...users.map((user) => headers.map((header) => escapeCsv(user[header])).join(',')),
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `uc-users-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

const DetailItem = ({ icon, label, value }) => (
  <Box className="uc-user-detail-item">
    <span>{icon}</span>
    <div>
      <small>{label}</small>
      <strong>{value || '—'}</strong>
    </div>
  </Box>
);

const AdminUserGrid = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [viewUser, setViewUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [accessFilter, setAccessFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortField, setSortField] = useState('modified_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [sortModel, setSortModel] = useState([{ field: 'modified_date', sort: 'desc' }]);
  const [notification, setNotification] = useState(null);

  const token = localStorage.getItem('token');

  const authHeaders = useMemo(() => (
    token ? { Authorization: `Bearer ${token}` } : {}
  ), [token]);

  const fetchUsers = useCallback(async () => {
    const res = await axios.get(`${API_BASE_URL}/api/admin/users`, {
      headers: authHeaders,
    });
    setUsers(Array.isArray(res.data) ? res.data : []);
  }, [authHeaders]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        await fetchUsers();
      } catch (error) {
        console.error(error);
        setNotification({
          type: 'error',
          message: 'Unable to load UC users. Please verify the admin API is running.',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [fetchUsers]);

  const metrics = useMemo(() => {
    const total = users.length;
    const admins = users.filter((user) => safeLower(user.accesslevel) === 'admin').length;
    const providers = users.filter((user) => booleanish(user.isprovider)).length;
    const pending = users.filter((user) => user.status === 'pending_approval').length;
    const incomplete = users.filter((user) => !booleanish(user.profileupdate)).length;

    return [
      {
        label: 'Total users',
        value: total,
        icon: <PeopleAlt />,
        tone: 'blue',
        helper: 'All registered UC accounts',
      },
      {
        label: 'Admins',
        value: admins,
        icon: <AdminPanelSettings />,
        tone: 'purple',
        helper: 'Users with admin access',
      },
      {
        label: 'Providers',
        value: providers,
        icon: <VerifiedUser />,
        tone: 'green',
        helper: 'Members who can provide help',
      },
      {
        label: 'Needs review',
        value: pending + incomplete,
        icon: <PersonSearch />,
        tone: 'orange',
        helper: `${pending} pending, ${incomplete} incomplete`,
      },
    ];
  }, [users]);

  const filteredUsers = useMemo(() => {
    const query = safeLower(searchTerm).trim();

    return users.filter((user) => {
      const matchesSearch = !query || safeLower(userSearchBlob(user)).includes(query);
      const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
      const matchesAccess = accessFilter === 'all' || safeLower(user.accesslevel) === accessFilter;
      const matchesRole = (() => {
        if (roleFilter === 'all') return true;
        if (roleFilter === 'provider') return booleanish(user.isprovider);
        if (roleFilter === 'founder') return booleanish(user.stack_holder);
        if (roleFilter === 'community') return Boolean(user.localcommunity);
        if (roleFilter === 'incomplete') return !booleanish(user.profileupdate);
        return true;
      })();

      return matchesSearch && matchesStatus && matchesAccess && matchesRole;
    });
  }, [accessFilter, roleFilter, searchTerm, statusFilter, users]);

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setAccessFilter('all');
    setRoleFilter('all');
    setSortField('modified_date');
    setSortDirection('desc');
    setSortModel([{ field: 'modified_date', sort: 'desc' }]);
  };

  const handleSortFieldChange = (value) => {
    setSortField(value);
    setSortModel([{ field: value, sort: sortDirection }]);
  };

  const handleSortDirectionChange = (value) => {
    setSortDirection(value);
    setSortModel([{ field: sortField, sort: value }]);
  };

  const handleSortModelChange = (model) => {
    setSortModel(model);
    if (model?.[0]) {
      setSortField(model[0].field);
      setSortDirection(model[0].sort || 'asc');
    }
  };

  const handleUnlock = useCallback(async (user) => {
    const confirmed = window.confirm(
      `Unlock ${user.first_name || user.email}? This will generate a temporary password and email the user.`
    );
    if (!confirmed) return;

    try {
      await axios.put(`${API_BASE_URL}/api/admin/users/unlock/${user.id}`, {}, {
        headers: authHeaders,
      });
      setNotification({ type: 'success', message: 'User account unlocked and email notification sent.' });
      await fetchUsers();
    } catch (error) {
      console.error(error);
      setNotification({ type: 'error', message: 'Unable to unlock this account.' });
    }
  }, [authHeaders, fetchUsers]);

  const handleSendActivation = useCallback(async (user) => {
    const confirmed = window.confirm(
      `Send a new secure password-setup email to ${user.first_name || user.email}?`
    );
    if (!confirmed) return;

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/users/${user.id}/send-activation`,
        {},
        { headers: authHeaders }
      );
      setNotification({
        type: 'success',
        message: response.data?.message || 'Secure password-setup email sent.',
      });
    } catch (error) {
      console.error(error);
      setNotification({
        type: 'error',
        message: error.response?.data?.message || 'Unable to send the password-setup email.',
      });
    }
  }, [authHeaders]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await axios.put(`${API_BASE_URL}/api/admin/users/${editUser.id}`, editUser, {
        headers: authHeaders,
      });
      setEditUser(null);
      await fetchUsers();
      setNotification({
        type: 'success',
        message: response.data?.message || 'User details updated successfully.',
      });
    } catch (error) {
      console.error(error);
      setNotification({ type: 'error', message: 'Unable to save user changes.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setEditUser((prev) => ({ ...prev, profile_photo: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const columns = useMemo(() => [
    {
      field: 'first_name',
      headerName: 'User',
      minWidth: 280,
      flex: 1.15,
      renderCell: (params) => (
        <Box className="uc-user-cell">
          <Avatar
            src={params.row.profile_photo || undefined}
            alt={initialsFor(params.row)}
            className="uc-user-avatar"
          >
            {initialsFor(params.row)}
          </Avatar>
          <div>
            <strong>{`${params.row.first_name || ''} ${params.row.last_name || ''}`.trim() || 'Unnamed user'}</strong>
            <small><MailOutline fontSize="inherit" /> {params.row.email || 'No email'}</small>
            {params.row.mobile && <small><PhoneIphone fontSize="inherit" /> {params.row.mobile}</small>}
            {params.row.status === 'approved' && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<MailOutline fontSize="small" />}
                onClick={(event) => {
                  event.stopPropagation();
                  handleSendActivation(params.row);
                }}
                sx={{ alignSelf: 'flex-start', mt: 0.75, textTransform: 'none' }}
              >
                Send setup email
              </Button>
            )}
          </div>
        </Box>
      ),
    },
    {
      field: 'company',
      headerName: 'Organization',
      minWidth: 240,
      flex: 1,
      renderCell: (params) => (
        <Box className="uc-user-org-cell">
          <strong>{params.row.company || 'No company listed'}</strong>
          <small>{params.row.industry || 'Industry not set'}</small>
          <small>{params.row.role || 'Role not set'}</small>
        </Box>
      ),
    },
    {
      field: 'accesslevel',
      headerName: 'Access',
      minWidth: 210,
      flex: 0.8,
      renderCell: (params) => (
        <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center">
          <Chip
            size="small"
            icon={safeLower(params.row.accesslevel) === 'admin' ? <AdminPanelSettings /> : <Badge />}
            label={params.row.accesslevel || 'user'}
            color={safeLower(params.row.accesslevel) === 'admin' ? 'primary' : 'default'}
            variant={safeLower(params.row.accesslevel) === 'admin' ? 'filled' : 'outlined'}
          />
          {booleanish(params.row.isprovider) && <Chip size="small" label="Provider" color="success" variant="outlined" />}
          {booleanish(params.row.stack_holder) && <Chip size="small" label="Founder" color="warning" variant="outlined" />}
        </Stack>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 170,
      renderCell: (params) => (
        <Chip
          label={statusLabel(params.row.status)}
          color={statusTone[params.row.status] || 'default'}
          size="small"
          className="uc-user-status-chip"
        />
      ),
    },
    {
      field: 'profileupdate',
      headerName: 'Profile',
      width: 160,
      renderCell: (params) => (
        <Stack gap={0.5}>
          <Chip
            size="small"
            label={booleanish(params.row.profileupdate) ? 'Complete' : 'Incomplete'}
            color={booleanish(params.row.profileupdate) ? 'success' : 'warning'}
            variant="outlined"
          />
          <Typography variant="caption" color="text.secondary">
            Disclaimer: {params.row.disclaimer_status || '—'}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'helpfields',
      headerName: 'Interest / Support',
      minWidth: 280,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const help = parseList(params.row.helpfields);
        const support = parseList(params.row.supporter_lst);
        const visible = [...help.slice(0, 2), ...support.slice(0, 2)];
        const hiddenCount = Math.max(0, help.length + support.length - visible.length);

        return visible.length ? (
          <Stack direction="row" gap={0.6} flexWrap="wrap" alignItems="center">
            {visible.map((item, index) => (
              <Chip key={`${item}-${index}`} label={item} size="small" variant="outlined" />
            ))}
            {hiddenCount > 0 && <Chip label={`+${hiddenCount}`} size="small" color="info" />}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">Not captured</Typography>
        );
      },
    },
    {
      field: 'modified_date',
      headerName: 'Last Update',
      width: 180,
      renderCell: (params) => (
        <Box className="uc-user-org-cell">
          <strong>{formatDate(params.row.modified_date)}</strong>
          <small>{params.row.modified_by ? `By ${params.row.modified_by}` : 'No editor recorded'}</small>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" gap={0.25}>
          <Tooltip title="View profile">
            <IconButton size="small" onClick={() => setViewUser(params.row)} aria-label="View user">
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit profile">
            <IconButton size="small" onClick={() => setEditUser({ ...params.row })} aria-label="Edit user">
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.status === 'approved' && (
            <Tooltip title="Resend password-setup email">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleSendActivation(params.row)}
                aria-label="Resend password-setup email"
              >
                <MailOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Unlock account">
            <IconButton size="small" onClick={() => handleUnlock(params.row)} aria-label="Unlock user">
              <LockOpen fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ], [handleSendActivation, handleUnlock]);

  if (isLoading) return <LoadingScreen />;

  return (
    <Box className="uc-user-management">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <Box className="uc-user-hero">
          <Box>
            <span>User Operations</span>
            <Typography variant="h4" component="h2">User Management</Typography>
            <Typography>
              Search, sort, review, and maintain Udyog Connect users from one professional admin workspace.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={() => exportUsersToCsv(filteredUsers)}
              disabled={!filteredUsers.length}
            >
              Export filtered
            </Button>
            <Button variant="contained" startIcon={<Refresh />} onClick={fetchUsers}>
              Refresh
            </Button>
          </Stack>
        </Box>

        <Grid container spacing={2} className="uc-user-metrics">
          {metrics.map((metric) => (
            <Grid item xs={12} sm={6} lg={3} key={metric.label}>
              <Paper className={`uc-user-metric-card ${metric.tone}`} elevation={0}>
                <span>{metric.icon}</span>
                <div>
                  <strong>{metric.value}</strong>
                  <small>{metric.label}</small>
                  <em>{metric.helper}</em>
                </div>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Paper className="uc-user-toolbar" elevation={0}>
          <TextField
            label="Search users"
            placeholder="Name, email, phone, company, role, support skill..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          <Grid container spacing={1.5}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Access</InputLabel>
                <Select
                  label="Access"
                  value={accessFilter}
                  onChange={(event) => setAccessFilter(event.target.value)}
                >
                  {ACCESS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>User type</InputLabel>
                <Select
                  label="User type"
                  value={roleFilter}
                  onChange={(event) => setRoleFilter(event.target.value)}
                >
                  {ROLE_FILTER_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                className="uc-user-clear-btn"
                fullWidth
                variant="text"
                startIcon={<RestartAlt />}
                onClick={clearFilters}
              >
                Reset filters
              </Button>
            </Grid>
          </Grid>

          <Box className="uc-user-sort-row">
            <FilterList />
            <Typography variant="body2">
              Showing <strong>{filteredUsers.length}</strong> of <strong>{users.length}</strong> users
            </Typography>
            <FormControl size="small" className="uc-user-sort-control">
              <InputLabel>Sort by</InputLabel>
              <Select
                label="Sort by"
                value={sortField}
                onChange={(event) => handleSortFieldChange(event.target.value)}
              >
                {SORT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" className="uc-user-sort-direction">
              <InputLabel>Order</InputLabel>
              <Select
                label="Order"
                value={sortDirection}
                onChange={(event) => handleSortDirectionChange(event.target.value)}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>

        <Paper className="uc-user-grid-card" elevation={0}>
          <DataGrid
            rows={filteredUsers}
            columns={columns}
            getRowId={(row) => row.id}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 },
              },
            }}
            getRowHeight={() => 'auto'}
            localeText={{
              noRowsLabel: 'No users match the current search and filters.',
            }}
            sx={{
              border: 0,
              height: '100%',
              '& .MuiDataGrid-columnHeaders': {
                background: '#f8fafc',
                borderBottom: '1px solid rgba(15, 23, 42, 0.1)',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 900,
                color: '#0f172a',
              },
              '& .MuiDataGrid-cell': {
                alignItems: 'center',
                borderColor: 'rgba(15, 23, 42, 0.07)',
                py: 1.25,
              },
              '& .MuiDataGrid-row': {
                minHeight: '86px !important',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f8fbff',
              },
              '& .MuiDataGrid-virtualScroller': {
                overflowY: 'auto',
              },
            }}
          />
        </Paper>
      </motion.div>

      <Dialog open={!!viewUser} onClose={() => setViewUser(null)} maxWidth="md" fullWidth>
        <DialogTitle className="uc-user-dialog-title">
          <Box className="uc-user-dialog-heading">
            <Avatar src={viewUser?.profile_photo || undefined}>{initialsFor(viewUser)}</Avatar>
            <div>
              <Typography variant="h6">{`${viewUser?.first_name || ''} ${viewUser?.last_name || ''}`.trim() || 'User details'}</Typography>
              <Typography variant="body2" color="text.secondary">{viewUser?.email}</Typography>
            </div>
          </Box>
          <IconButton onClick={() => setViewUser(null)} aria-label="Close view user dialog">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {viewUser && (
            <Stack spacing={2.5}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <DetailItem icon={<MailOutline />} label="Email" value={viewUser.email} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailItem icon={<PhoneIphone />} label="Mobile" value={viewUser.mobile} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailItem icon={<CorporateFare />} label="Company / Industry" value={`${viewUser.company || 'No company'} · ${viewUser.industry || 'No industry'}`} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailItem icon={<Badge />} label="Role" value={viewUser.role} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailItem icon={<ManageAccounts />} label="Status" value={statusLabel(viewUser.status)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailItem icon={<Groups />} label="Access" value={viewUser.accesslevel || 'user'} />
                </Grid>
              </Grid>

              <Divider />

              <Box>
                <Typography variant="subtitle2" gutterBottom>Captured interests and support areas</Typography>
                <Stack direction="row" gap={0.75} flexWrap="wrap">
                  {[...parseList(viewUser.helpfields), ...parseList(viewUser.supporter_lst)].length ? (
                    [...parseList(viewUser.helpfields), ...parseList(viewUser.supporter_lst)].map((item, index) => (
                      <Chip key={`${item}-${index}`} label={item} size="small" />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">No interest data captured yet.</Typography>
                  )}
                </Stack>
              </Box>

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <DetailItem icon={<VerifiedUser />} label="Profile update" value={booleanish(viewUser.profileupdate) ? 'Complete' : 'Incomplete'} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailItem icon={<Edit />} label="Last modified" value={`${formatDate(viewUser.modified_date)}${viewUser.modified_by ? ` by ${viewUser.modified_by}` : ''}`} />
                </Grid>
              </Grid>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewUser(null)}>Close</Button>
          {viewUser && (
            <>
              {viewUser.status === 'approved' && (
                <Button
                  variant="outlined"
                  startIcon={<MailOutline />}
                  onClick={() => handleSendActivation(viewUser)}
                >
                  Send setup email
                </Button>
              )}
              <Button
                variant="contained"
                onClick={() => {
                  setEditUser({ ...viewUser });
                  setViewUser(null);
                }}
              >
                Edit user
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={!!editUser} onClose={() => setEditUser(null)} fullWidth maxWidth="md">
        <DialogTitle className="uc-user-dialog-title">
          <Box>
            <Typography variant="h6">Edit user access and profile</Typography>
            <Typography variant="body2" color="text.secondary">{editUser?.email}</Typography>
          </Box>
          <IconButton onClick={() => setEditUser(null)} aria-label="Close edit user dialog">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {editUser && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box className="uc-user-photo-editor">
                  <Avatar src={editUser.profile_photo || undefined}>{initialsFor(editUser)}</Avatar>
                  <Typography variant="subtitle2">{`${editUser.first_name || ''} ${editUser.last_name || ''}`.trim() || 'UC User'}</Typography>
                  <Typography variant="body2" color="text.secondary">{editUser.email}</Typography>
                  <Button variant="outlined" component="label" size="small">
                    Upload photo
                    <input type="file" hidden accept="image/*" onChange={handlePhotoUpload} />
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={8}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Status"
                      value={editUser.status || ''}
                      onChange={(event) => setEditUser({ ...editUser, status: event.target.value })}
                      fullWidth
                    >
                      {STATUS_OPTIONS.filter((option) => option.value !== 'all').map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Access level"
                      value={editUser.accesslevel || 'user'}
                      onChange={(event) => setEditUser({ ...editUser, accesslevel: event.target.value })}
                      fullWidth
                    >
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="user">User</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Company"
                      value={editUser.company || ''}
                      onChange={(event) => setEditUser({ ...editUser, company: event.target.value })}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Industry"
                      value={editUser.industry || ''}
                      onChange={(event) => setEditUser({ ...editUser, industry: event.target.value })}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Role"
                      value={editUser.role || ''}
                      onChange={(event) => setEditUser({ ...editUser, role: event.target.value })}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Provider"
                      value={Number(booleanish(editUser.isprovider))}
                      onChange={(event) => setEditUser({ ...editUser, isprovider: Number(event.target.value) })}
                      fullWidth
                    >
                      <MenuItem value={1}>Yes</MenuItem>
                      <MenuItem value={0}>No</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Founder"
                      value={Number(booleanish(editUser.stack_holder))}
                      onChange={(event) => setEditUser({ ...editUser, stack_holder: Number(event.target.value) })}
                      fullWidth
                    >
                      <MenuItem value={1}>Yes</MenuItem>
                      <MenuItem value={0}>No</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUser(null)}>Cancel</Button>
          {editUser?.status === 'approved' && (
            <Button
              variant="outlined"
              startIcon={<MailOutline />}
              onClick={() => handleSendActivation(editUser)}
            >
              Send setup email
            </Button>
          )}
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<Save />}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!notification}
        autoHideDuration={4000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {notification ? (
          <Alert severity={notification.type} variant="filled" onClose={() => setNotification(null)}>
            {notification.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  );
};

export default AdminUserGrid;
